import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getBackendClient } from "@/lib/backend";
import { logger } from "@/lib/logging";
import { appendAudioLevel } from "@/lib/voice/audio-level";
import {
  advanceVoiceStatus,
  idleVoiceConversation,
  type VoiceConversationState,
  voiceConversationFailure,
} from "@/lib/voice/conversation";
import {
  describeConnectionError,
  describeMicrophoneError,
  OperatorFacingError,
  operatorMessageForMintError,
  readStatusCode,
  reportVoiceFailure,
  type VoiceCloseDetail,
  type VoiceFailureStage,
  voiceFailureStageOf,
} from "@/lib/voice/media-errors";

/**
 * Whole-handshake budget: credential mint plus transport negotiation. Provider
 * sockets can accept a TCP connection and then stall forever, which would
 * otherwise pin the session in `connecting` with a live microphone.
 */
const CONNECT_TIMEOUT_MS = 15_000;
const CONNECT_TIMEOUT_MESSAGE =
  "The voice service did not respond in time. Check your network and try again.";
const SEND_TEXT_FAILURE_MESSAGE =
  "Your message could not be sent because the voice session is not connected. Try again.";

/** Appends the adapter's lost-connection guidance as its own sentence. */
export function withGuidance(message: string, guidance?: string): string {
  if (!guidance) return message;
  const sentence = message.trim();
  return `${/[.!?]$/.test(sentence) ? sentence : `${sentence}.`} ${guidance}`;
}
const AUDIO_LEVEL_HISTORY_SIZE = 28;
const VOICE_CAPTURE_CONSTRAINTS = {
  audio: {
    autoGainControl: { ideal: true },
    echoCancellation: { ideal: true },
    noiseSuppression: { ideal: true },
  },
} satisfies MediaStreamConstraints;

const backend = getBackendClient();

/** Callbacks a provider transport uses to report events and failures upward. */
export interface VoiceSessionControls {
  /** Ends a provider-completed session without reporting a transport failure. */
  endConnection: () => void;
  fail: (message: string) => void;
  /**
   * Reports a lost transport. `closeDetail` carries the provider socket's
   * sanitized close code and reason so the failure is diagnosable.
   */
  loseConnection: (message: string, cause?: unknown, closeDetail?: VoiceCloseDetail) => void;
  publishAudioLevel: (level: number) => void;
  read: () => VoiceConversationState;
  update: (updater: (current: VoiceConversationState) => VoiceConversationState) => void;
}

/**
 * An open provider transport. `release` must be safe to call more than once,
 * and `sendText` reports whether the provider actually accepted the message —
 * holding a transport is not proof its socket is still writable.
 */
export interface VoiceSessionTransport {
  release: () => void;
  sendText?: (text: string) => boolean;
  /** Stops and suppresses provider output without changing microphone capture. */
  setOutputMuted?: (muted: boolean) => void;
}

/**
 * Why a typed message did or did not reach the provider. `unsupported` is a
 * capability fact about the provider, not a broken session, so it is kept
 * distinct from a transport that is missing (`closed`) or that rejected the
 * frame (`refused`).
 */
type TextDispatch = "closed" | "refused" | "sent" | "unsupported";

/** Everything one provider must supply to run on the shared session lifecycle. */
export interface VoiceSessionAdapter<TCredentials> {
  connect: (input: {
    controls: VoiceSessionControls;
    credentials: TCredentials;
    microphone: MediaStream;
  }) => Promise<VoiceSessionTransport>;
  /** Added to every lost-connection message; a lost connection never retries a request. */
  disconnectGuidance?: string;
  functionName: string;
  incompleteSessionMessage: string;
  isSupported: () => boolean;
  /** Decodes even expired results for cleanup; must not claim or publish ownership. */
  parseCredentials: (payload: unknown) => TCredentials | null;
  /** Claims only credentials admitted for the current, mounted session attempt. */
  claimCredentials: (credentials: TCredentials) => void;
  /** The mint request body; empty when absent. */
  sessionRequest?: () => Record<string, unknown>;
  /** Told the HTTP status of a failed mint (null when unknown) before the failure is shown. */
  /** The mint was refused: its HTTP status and the error the backend returned. */
  mintFailed?: (status: number | null, error: unknown) => void;
  releaseCredentials?: (credentials: TCredentials) => void;
  sessionFailureMessage: string;
  unsupportedMessage: string;
}

export type VoiceSession = VoiceConversationState & {
  /** Recent live input/output levels, normalized to 0..1 and newest last. */
  levels: number[];
  /** Live capture stream while a session is open, for audio visualisation. */
  microphone: MediaStream | null;
  /** Whether the active capture tracks are disabled. */
  muted: boolean;
  /** Whether received provider audio is being discarded. */
  outputMuted: boolean;
  start: () => Promise<void>;
  sendText: (text: string) => Promise<boolean>;
  stop: () => void;
  toggle: () => void;
  toggleMute: () => void;
  toggleOutputMute: () => void;
};

async function requestSessionUnbounded<TCredentials>(
  adapter: VoiceSessionAdapter<TCredentials>,
): Promise<TCredentials> {
  const { data, error } = await backend.functions.invoke<unknown>(adapter.functionName, {
    body: adapter.sessionRequest?.() ?? {},
  });

  if (error) {
    logger.error({ err: error, scope: "voice.session" }, "session mint refused");
    adapter.mintFailed?.(readStatusCode(error), error);
    throw new OperatorFacingError(
      operatorMessageForMintError(error, adapter.sessionFailureMessage),
      "mint",
    );
  }

  const credentials = adapter.parseCredentials(data);
  if (!credentials) {
    throw new OperatorFacingError(adapter.incompleteSessionMessage, "mint");
  }
  return credentials;
}

/** A late credential is revoked even when its request outlives the UI deadline. */
async function requestSession<TCredentials>(
  adapter: VoiceSessionAdapter<TCredentials>,
  timeoutMs: number,
): Promise<TCredentials> {
  let expired = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const pending = requestSessionUnbounded(adapter).then((credentials) => {
    if (expired) adapter.releaseCredentials?.(credentials);
    return credentials;
  });
  try {
    return await Promise.race([
      pending,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          expired = true;
          reject(new OperatorFacingError(CONNECT_TIMEOUT_MESSAGE));
        }, timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function remainingConnectTime(deadline: number) {
  const remaining = deadline - Date.now();
  if (remaining <= 0) throw new OperatorFacingError(CONNECT_TIMEOUT_MESSAGE);
  return remaining;
}

/**
 * Bounds a handshake. A transport that arrives after the deadline has no owner,
 * so it is released rather than left holding a socket and an audio context.
 */
function withConnectDeadline(
  connecting: Promise<VoiceSessionTransport>,
  timeoutMs: number,
): Promise<VoiceSessionTransport> {
  return new Promise<VoiceSessionTransport>((resolve, reject) => {
    const timer = setTimeout(() => {
      connecting
        .then((transport) => transport.release())
        .catch((error: unknown) => {
          logger.error(
            { err: error, scope: "voice.session" },
            "late transport could not be released",
          );
        });
      reject(new OperatorFacingError(CONNECT_TIMEOUT_MESSAGE));
    }, timeoutMs);

    connecting.then(
      (transport) => {
        clearTimeout(timer);
        resolve(transport);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

function stopTracks(microphone: MediaStream): void {
  for (const track of microphone.getTracks()) {
    track.stop();
  }
}

interface TextDispatchContext {
  controls: VoiceSessionControls;
  isOpen: () => boolean;
  read: () => VoiceConversationState;
  settled: () => Promise<void>;
  start: () => Promise<void>;
  transportSendText: (text: string) => TextDispatch;
}

function useTextDispatch({
  controls,
  isOpen,
  read,
  settled,
  start,
  transportSendText,
}: TextDispatchContext): (text: string) => Promise<boolean> {
  return useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) {
        return false;
      }
      if (!isOpen()) {
        await start();
        await settled();
      }
      const dispatch = transportSendText(trimmed);
      if (dispatch === "sent") {
        return true;
      }
      if (dispatch === "unsupported") {
        logger.warn(
          { scope: "voice.session" },
          "provider transport does not accept typed messages",
        );
        return false;
      }
      const current = read();
      controls.loseConnection(
        current.status === "error" ? current.errorMessage : SEND_TEXT_FAILURE_MESSAGE,
      );
      return false;
    },
    [controls, isOpen, read, settled, start, transportSendText],
  );
}

/** Acquires capture, or reports why it could not and yields null. */
async function captureMicrophone(
  onFailure: (error: unknown, message: string) => void,
): Promise<MediaStream | null> {
  try {
    return await navigator.mediaDevices.getUserMedia(VOICE_CAPTURE_CONSTRAINTS);
  } catch (error) {
    onFailure(error, describeMicrophoneError(error));
    return null;
  }
}

/** Mints credentials and opens the provider transport under one deadline. */
function openTransport<TCredentials>({
  adapter,
  controls,
  credentials,
  microphone,
  timeoutMs,
}: {
  adapter: VoiceSessionAdapter<TCredentials>;
  controls: VoiceSessionControls;
  credentials: TCredentials;
  microphone: MediaStream;
  timeoutMs: number;
}): Promise<VoiceSessionTransport> {
  return withConnectDeadline(
    (async () =>
      await adapter.connect({
        controls,
        credentials,
        microphone,
      }))(),
    timeoutMs,
  );
}

/**
 * Sole owner of the capture stream. Keeping the ref and the state value in one
 * place is what stops a teardown path from stopping the tracks while the
 * visualiser still holds a handle to them.
 */
function useMicrophoneSlot() {
  const [microphone, setMicrophone] = useState<MediaStream | null>(null);
  const [muted, setMuted] = useState(false);
  const microphoneRef = useRef<MediaStream | null>(null);

  const claim = useCallback((stream: MediaStream) => {
    for (const track of stream.getAudioTracks()) {
      track.enabled = true;
    }
    microphoneRef.current = stream;
    setMicrophone(stream);
    setMuted(false);
  }, []);

  const release = useCallback(() => {
    const stream = microphoneRef.current;
    microphoneRef.current = null;
    setMicrophone(null);
    setMuted(false);
    if (stream) {
      stopTracks(stream);
    }
  }, []);

  const toggleMute = useCallback((): boolean => {
    const tracks = microphoneRef.current?.getAudioTracks() ?? [];
    if (tracks.length === 0) {
      return false;
    }

    const nextMuted = tracks.some((track) => track.enabled !== false);
    for (const track of tracks) {
      track.enabled = !nextMuted;
    }
    setMuted(nextMuted);
    return nextMuted;
  }, []);

  return { claim, microphone, muted, release, toggleMute };
}

/** Live, transport-driven samples for the orb and waveform. */
function useAudioLevelHistory() {
  const [levels, setLevels] = useState<number[]>([]);
  const clear = useCallback(() => setLevels([]), []);
  const publish = useCallback((level: number) => {
    setLevels((current) => appendAudioLevel(current, level, AUDIO_LEVEL_HISTORY_SIZE));
  }, []);

  return { clear, levels, publish };
}

/**
 * The conversation half of a session: the rendered value, the ref transports
 * fold events against, and the disposal guard that drops writes after unmount.
 */
function useConversationState() {
  const [conversation, setConversation] = useState<VoiceConversationState>(idleVoiceConversation);
  const conversationRef = useRef<VoiceConversationState>(idleVoiceConversation);
  const disposedRef = useRef(false);

  // Transports fold events with side effects (audio playback, keepalives), so
  // they read the current state through a ref instead of a state updater React
  // may replay.
  const update = useCallback(
    (updater: (current: VoiceConversationState) => VoiceConversationState) => {
      if (disposedRef.current) {
        return;
      }
      const next = updater(conversationRef.current);
      conversationRef.current = next;
      setConversation(next);
    },
    [],
  );

  const fail = useCallback(
    (message: string) => {
      update((current) => voiceConversationFailure(current, message));
    },
    [update],
  );

  const read = useCallback(() => conversationRef.current, []);

  return { conversation, disposedRef, fail, read, update };
}

/** Everything the start sequence needs from the hook's slots. */
interface StartContext<TCredentials> {
  adapter: VoiceSessionAdapter<TCredentials>;
  adopt: (transport: VoiceSessionTransport) => void;
  claim: (stream: MediaStream) => void;
  controls: VoiceSessionControls;
  disposed: () => boolean;
  fail: (message: string) => void;
  generation: () => number;
  isOpen: () => boolean;
  releaseTransport: () => void;
  report: (stage: VoiceFailureStage, cause?: unknown) => void;
  update: (updater: (current: VoiceConversationState) => VoiceConversationState) => void;
}

/** Acquires and hands off the resources for one session attempt. */
async function establishTransport<TCredentials>(
  context: StartContext<TCredentials>,
  startedAt: number,
  deadline: number,
): Promise<void> {
  const { adapter, controls, fail, releaseTransport, report, update } = context;
  let credentials: TCredentials | undefined;
  let transportOwnsCredentials = false;
  try {
    // Validate the owned live capability before requesting microphone permission.
    credentials = await requestSession(adapter, remainingConnectTime(deadline));
    if (isStartAbandoned(context, startedAt)) {
      adapter.releaseCredentials?.(credentials);
      return;
    }
    adapter.claimCredentials(credentials);
    const capture = await captureMicrophone((error, message) => {
      report("microphone", error);
      fail(message);
    });
    if (!capture) {
      adapter.releaseCredentials?.(credentials);
      return;
    }
    context.claim(capture);
    if (isStartAbandoned(context, startedAt)) {
      releaseTransport();
      adapter.releaseCredentials?.(credentials);
      return;
    }
    const transport = await openTransport({
      adapter,
      controls,
      credentials,
      microphone: capture,
      timeoutMs: remainingConnectTime(deadline),
    });
    // `adopt` stores the transport synchronously before it applies optional
    // provider output controls, transferring conversation cleanup to release.
    transportOwnsCredentials = true;
    context.adopt(transport);

    // `stop()` and a lost connection both run to completion while this
    // handshake is still pending, and `advanceVoiceStatus` refuses to leave the
    // `idle`/`error` state they left behind. Keeping the transport here would
    // hold a live, credential-bearing provider session open — streaming silence,
    // since the capture is already released — behind a UI that reads as stopped.
    if (isStartAbandoned(context, startedAt)) {
      releaseTransport();
      return;
    }
    update((current) => advanceVoiceStatus(current, "listening"));
  } catch (error) {
    if (!transportOwnsCredentials && credentials !== undefined) {
      adapter.releaseCredentials?.(credentials);
    }
    // Snapshot the failing state before teardown rewrites it.
    report(voiceFailureStageOf(error), error);
    releaseTransport();
    fail(describeConnectionError(error));
  }
}

/**
 * Capability check, capture, credential mint, provider connect, settle. Lives
 * outside the hook so the sequence reads top to bottom and the hook itself
 * stays wiring.
 */
async function runStart<TCredentials>(
  context: StartContext<TCredentials>,
  markStarting: (starting: boolean) => void,
): Promise<void> {
  const { adapter, fail, generation, report, update } = context;
  if (!adapter.isSupported()) {
    report("unsupported");
    fail(adapter.unsupportedMessage);
    return;
  }

  markStarting(true);
  update(() => ({ status: "connecting", turns: [] }));
  try {
    await establishTransport(context, generation(), Date.now() + CONNECT_TIMEOUT_MS);
  } finally {
    markStarting(false);
  }
}

function dispatchTransportText(
  transport: VoiceSessionTransport | null,
  text: string,
): TextDispatch {
  if (!transport) return "closed";
  if (!transport.sendText) return "unsupported";
  return transport.sendText(text) ? "sent" : "refused";
}

/**
 * Owns the open provider transport and pairs its disposal with the
 * microphone's. `release` never throws: a provider that fails to tear down
 * must not strand the capture indicator or the UI.
 */
function useTransportSlot(
  releaseMicrophone: () => void,
  clearAudioLevels: () => void,
  readOutputMuted: () => boolean,
  resetOutputMute: () => void,
) {
  const transportRef = useRef<VoiceSessionTransport | null>(null);

  const adopt = useCallback(
    (transport: VoiceSessionTransport) => {
      transportRef.current = transport;
      transport.setOutputMuted?.(readOutputMuted());
    },
    [readOutputMuted],
  );

  const isOpen = useCallback(() => transportRef.current !== null, []);

  const sendText = useCallback((text: string): TextDispatch => {
    return dispatchTransportText(transportRef.current, text);
  }, []);

  const setOutputMuted = useCallback((muted: boolean) => {
    transportRef.current?.setOutputMuted?.(muted);
  }, []);

  const release = useCallback(() => {
    const transport = transportRef.current;
    transportRef.current = null;

    try {
      transport?.release();
    } catch (error) {
      logger.error({ err: error, scope: "voice.session" }, "transport release failed");
    } finally {
      releaseMicrophone();
      clearAudioLevels();
      resetOutputMute();
    }
  }, [clearAudioLevels, releaseMicrophone, resetOutputMute]);

  return { adopt, isOpen, release, sendText, setOutputMuted };
}

/** Session-owned output gate. Unlike microphone mute it preserves capture and socket state. */
function useOutputMuteSlot() {
  const [outputMuted, setOutputMuted] = useState(false);
  const outputMutedRef = useRef(false);

  const set = useCallback((muted: boolean) => {
    outputMutedRef.current = muted;
    setOutputMuted(muted);
    return muted;
  }, []);
  const reset = useCallback(() => set(false), [set]);
  const toggle = useCallback(() => set(!outputMutedRef.current), [set]);
  const read = useCallback(() => outputMutedRef.current, []);

  return { outputMuted, read, reset, toggle };
}

/**
 * True when a settled handshake has no owner left to hand its transport to,
 * because the surface unmounted or the session was already ended.
 */
function isStartAbandoned<TCredentials>(
  context: StartContext<TCredentials>,
  startedAt: number,
): boolean {
  return context.disposed() || context.generation() !== startedAt;
}

/**
 * Monotonic counter identifying the current session attempt. Every path that
 * ends a session invalidates it, which is how a handshake still in flight
 * learns that its result is no longer wanted.
 */
function useSessionGeneration() {
  const generationRef = useRef(0);

  const generation = useCallback(() => generationRef.current, []);
  const invalidate = useCallback(() => {
    generationRef.current += 1;
  }, []);

  return { generation, invalidate };
}

/**
 * Structured failure reporter for one session. The snapshot is read at call
 * time, so callers report before teardown rewrites the state the failure
 * actually interrupted.
 */
function useVoiceFailureReporter(input: {
  adapterName: string;
  disposedRef: { current: boolean };
  isOpen: () => boolean;
  read: () => VoiceConversationState;
}) {
  const { adapterName, disposedRef, isOpen, read } = input;

  return useCallback(
    (stage: VoiceFailureStage, cause?: unknown, closeDetail?: VoiceCloseDetail) => {
      reportVoiceFailure(
        stage,
        {
          adapter: adapterName,
          disposed: disposedRef.current,
          status: read().status,
          transportOpen: isOpen(),
        },
        cause,
        closeDetail,
      );
    },
    [adapterName, disposedRef, isOpen, read],
  );
}

/** What a transport is handed, plus the operator's stop command. */
function useSessionControls(input: {
  disconnectGuidance?: string;
  fail: (message: string) => void;
  invalidate: () => void;
  publishAudioLevel: (level: number) => void;
  read: () => VoiceConversationState;
  releaseTransport: () => void;
  report: (stage: VoiceFailureStage, cause?: unknown, closeDetail?: VoiceCloseDetail) => void;
  update: (updater: (current: VoiceConversationState) => VoiceConversationState) => void;
}) {
  const {
    disconnectGuidance,
    fail,
    invalidate,
    publishAudioLevel,
    read,
    releaseTransport,
    report,
    update,
  } = input;

  const stop = useCallback(() => {
    invalidate();
    releaseTransport();
    update((current) => ({ status: "idle", turns: current.turns }));
  }, [invalidate, releaseTransport, update]);

  const loseConnection = useCallback(
    (message: string, cause?: unknown, closeDetail?: VoiceCloseDetail) => {
      report("transport", cause, closeDetail);
      invalidate();
      releaseTransport();
      fail(withGuidance(message, disconnectGuidance));
    },
    [disconnectGuidance, fail, invalidate, releaseTransport, report],
  );

  const controls = useMemo<VoiceSessionControls>(
    () => ({ endConnection: stop, fail, loseConnection, publishAudioLevel, read, update }),
    [fail, loseConnection, publishAudioLevel, read, stop, update],
  );

  return { controls, stop };
}

/**
 * A session may only be started from a clean slate: no open transport, no
 * handshake already running, and the surface still mounted.
 */
function canStartSession(input: {
  disposed: () => boolean;
  isOpen: () => boolean;
  starting: boolean;
}): boolean {
  if (input.isOpen()) {
    return false;
  }
  if (input.starting) {
    return false;
  }
  return !input.disposed();
}

/**
 * The operator-facing commands. Owns the in-flight flag, so a second tap during
 * a handshake cannot open a second transport.
 */
function useSessionCommands<TCredentials>(
  input: StartContext<TCredentials> & { stop: () => void },
) {
  const { disposed, isOpen, stop } = input;
  const startingRef = useRef(false);
  const pendingStartRef = useRef<Promise<void> | null>(null);

  const start = useCallback(async () => {
    if (!canStartSession({ disposed, isOpen, starting: startingRef.current })) {
      return;
    }
    const running = runStart(input, (starting) => {
      startingRef.current = starting;
    });
    pendingStartRef.current = running;
    try {
      await running;
    } finally {
      // Only the handshake that published this promise may retract it, so a
      // slower predecessor cannot null a newer one and let a joiner resolve
      // against a transport that does not exist yet.
      if (pendingStartRef.current === running) {
        pendingStartRef.current = null;
      }
    }
  }, [disposed, input, isOpen]);

  /**
   * Resolves once any handshake in flight has settled. `start` deliberately
   * declines to duplicate one and returns immediately, so callers that need a
   * transport to exist — typed input — wait on this instead. `runStart` reports
   * its own failures through `fail`, so a joiner needs the settlement only.
   */
  const settled = useCallback(async () => {
    await pendingStartRef.current?.catch(() => undefined);
  }, []);

  const toggle = useCallback(() => {
    if (startingRef.current) {
      return;
    }
    if (isOpen()) {
      stop();
      return;
    }
    start().catch((error: unknown) => {
      logger.error({ err: error, scope: "voice.session" }, "voice session start threw");
    });
  }, [isOpen, start, stop]);

  return { settled, start, toggle };
}

/** Audio history that ignores transport callbacks after the provider unmounts. */
function useLiveAudioLevelHistory(disposedRef: { current: boolean }) {
  const { clear, levels, publish } = useAudioLevelHistory();
  const clearLive = useCallback(() => {
    if (!disposedRef.current) {
      clear();
    }
  }, [clear, disposedRef]);
  const publishLive = useCallback(
    (level: number) => {
      if (!disposedRef.current) {
        publish(level);
      }
    },
    [disposedRef, publish],
  );

  return { clear: clearLive, levels, publish: publishLive };
}

/** Capture, transport, audio-history, and generation slots for one session. */
function useSessionResources(disposedRef: { current: boolean }) {
  const audio = useLiveAudioLevelHistory(disposedRef);
  const microphone = useMicrophoneSlot();
  const output = useOutputMuteSlot();
  const transport = useTransportSlot(microphone.release, audio.clear, output.read, output.reset);
  const generation = useSessionGeneration();

  return { audio, generation, microphone, output, transport };
}

type ConversationSlot = ReturnType<typeof useConversationState>;
type SessionResources = ReturnType<typeof useSessionResources>;

interface StartContextInput<TCredentials> {
  adapter: VoiceSessionAdapter<TCredentials>;
  conversation: ConversationSlot;
  controls: VoiceSessionControls;
  report: (stage: VoiceFailureStage, cause?: unknown) => void;
  resources: SessionResources;
  stop: () => void;
}

/** Stable start-sequence wiring assembled from the hook's focused slots. */
function useStartContext<TCredentials>({
  adapter,
  conversation,
  controls,
  report,
  resources,
  stop,
}: StartContextInput<TCredentials>) {
  const { disposedRef, fail, update } = conversation;
  const { generation: readGeneration } = resources.generation;
  const { claim } = resources.microphone;
  const { adopt, isOpen, release: releaseTransport } = resources.transport;
  const disposed = useCallback(() => disposedRef.current, [disposedRef]);

  return useMemo(
    () => ({
      adapter,
      adopt,
      claim,
      controls,
      disposed,
      fail,
      generation: readGeneration,
      isOpen,
      releaseTransport,
      report,
      stop,
      update,
    }),
    [
      adapter,
      adopt,
      claim,
      controls,
      disposed,
      fail,
      isOpen,
      readGeneration,
      releaseTransport,
      report,
      stop,
      update,
    ],
  );
}

/** Releases every browser-owned resource when the shared provider unmounts. */
function useSessionDisposal(disposedRef: { current: boolean }, releaseTransport: () => void) {
  useEffect(() => {
    // StrictMode runs setup, cleanup, then setup again on the first mount, so
    // disposal has to be re-armed here or every later state write is dropped.
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      releaseTransport();
    };
  }, [disposedRef, releaseTransport]);
}

/** Session commands built on top of the state and resource slots. */
function useSessionOperations<TCredentials>(
  adapter: VoiceSessionAdapter<TCredentials>,
  conversation: ConversationSlot,
  resources: SessionResources,
) {
  const { fail, read, update } = conversation;
  const { clear: clearAudioLevels, publish: publishAudioLevel } = resources.audio;
  const { invalidate } = resources.generation;
  const { toggleMute: toggleMicrophoneMute } = resources.microphone;
  const { toggle: toggleOutputMuteState } = resources.output;
  const {
    isOpen,
    release: releaseTransport,
    sendText: transportSendText,
    setOutputMuted,
  } = resources.transport;
  const report = useVoiceFailureReporter({
    adapterName: adapter.functionName,
    disposedRef: conversation.disposedRef,
    isOpen,
    read,
  });
  const { controls, stop } = useSessionControls({
    disconnectGuidance: adapter.disconnectGuidance,
    fail,
    invalidate,
    publishAudioLevel,
    read,
    releaseTransport,
    report,
    update,
  });
  const startContext = useStartContext({
    adapter,
    conversation,
    controls,
    report,
    resources,
    stop,
  });
  const { settled, start, toggle } = useSessionCommands(startContext);
  const toggleMute = useCallback(() => {
    if (toggleMicrophoneMute()) {
      clearAudioLevels();
    }
  }, [clearAudioLevels, toggleMicrophoneMute]);
  const toggleOutputMute = useCallback(() => {
    setOutputMuted(toggleOutputMuteState());
  }, [setOutputMuted, toggleOutputMuteState]);
  const sendText = useTextDispatch({
    controls,
    isOpen,
    read,
    settled,
    start,
    transportSendText,
  });

  useSessionDisposal(conversation.disposedRef, releaseTransport);
  return { sendText, start, stop, toggle, toggleMute, toggleOutputMute };
}

/**
 * Runs the lifecycle every browser voice surface shares: capability check,
 * microphone capture, edge-minted credentials, provider connect, and teardown.
 *
 * The provider owns only its transport. Microphone ownership, reentrancy,
 * disposal, and the handshake deadline live here, so no failure path can leave
 * the browser recording indicator lit or the control permanently unresponsive.
 */
export function useVoiceSession<TCredentials>(
  adapter: VoiceSessionAdapter<TCredentials>,
): VoiceSession {
  const conversation = useConversationState();
  const resources = useSessionResources(conversation.disposedRef);
  const operations = useSessionOperations(adapter, conversation, resources);

  return {
    ...conversation.conversation,
    levels: resources.audio.levels,
    microphone: resources.microphone.microphone,
    muted: resources.microphone.muted,
    outputMuted: resources.output.outputMuted,
    ...operations,
  };
}
